import React, { useEffect, useState } from "react";
import styles from "./YoutubeVideoManager.module.css";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress"; // For loader
import { toast } from "react-toastify"; // For toast notifications
import { useAuth } from "../../../contexts/AuthContext";
import Dialog from "@mui/material/Dialog"; // Modal component
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button"; // Button component
import { toQueryString } from "../../../util/utilFunctions"; // Utility function for query strings
import { smTypes } from "../../../util/flStates"; // Constants for social media types

function YoutubeVideoManager() {
  const { accessToken } = useAuth();
  const [videos, setVideos] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [videoData, setVideoData] = useState({ type: "YouTube", url: "" });
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const [errorMessage, setErrorMessage] = useState(""); // Error message state
  const [openDialog, setOpenDialog] = useState(false); // Modal state
  const [selectedVideoId, setSelectedVideoId] = useState(null); // ID of selected video for deletion

  const handleInputChange = (e) => {
    setVideoData({ ...videoData, [e.target.name]: e.target.value });
  };

  const addVideo = async () => {
    if (!videoData.url.trim()) {
      setErrorMessage("Video URL cannot be empty.");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const requestData = { typeSMId: 3, url: videoData.url };
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/add`,
        requestData,
        {
          headers: { Authorization: accessToken },
        }
      );

      if (response.status === 200 && response.data.success) {
        if (editIndex !== null) {
          const updatedVideos = [...videos];
          updatedVideos[editIndex] = videoData;
          setVideos(updatedVideos);
          setEditIndex(null);
          toast.success("Video updated successfully!");
        } else {
          setVideos([...videos, { ...videoData, id: response.data.id }]);
          toast.success("Video added successfully!");
        }
        setVideoData({ type: "YouTube", url: "" });
      } else {
        const errorMsg = response.data.message || "Failed to upload the video.";
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        "Error uploading the video. Please try again!";
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const editVideo = (index) => {
    setEditIndex(index);
    setVideoData(videos[index]);
  };

  // Open confirmation modal before deletion
  const handleDeleteClick = (videoId) => {
    setSelectedVideoId(videoId);
    setOpenDialog(true);
  };

  // Close modal
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVideoId(null);
  };

  const confirmDelete = async () => {
    try {
      const requestData = { id: selectedVideoId };

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/delete/`,
        requestData,
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        setVideos(videos.filter((video) => video.id !== selectedVideoId));
        toast.success("Video deleted successfully!");
        handleCloseDialog();
      } else {
        const errorMsg = response.data.message || "Failed to delete the video.";
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error("Failed to delete the video. Please try again.");
    }
  };

  const getEmbedUrl = (url) => {
    if (url) {
      const videoId = url.split("v=")[1] || url.split("/").slice(-1)[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null;
  };

  // Fetch videos from the API
  useEffect(() => {
    const fetchVideos = async (accessToken) => {
      try {
        const queryString = toQueryString({
          typeSMId: smTypes.youtubeShort,
        });

        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/list?${queryString}`,
          {
            headers: {
              Authorization: accessToken,
            },
          }
        );

        if (response.status === 200 && response.data && response.data.success) {
          setVideos(response.data.smLinks);
        } else {
          const errorMsg =
            response.data.message || "Failed to fetch the videos.";
          setErrorMessage(errorMsg);
          toast.error(errorMsg);
        }
      } catch (error) {
        toast.error("Error while fetching YouTube videos. Please try again.");
      }
    };

    if (accessToken) {
      fetchVideos(accessToken);
    }

    return () => {};
  }, [accessToken]);

  return (
    <div className={styles.container}>
      <h3 className={styles.heading}>Manage YouTube Videos</h3>
      <div className={styles.instructions}>
        <h4>How to Add Your YouTube Videos or Shorts:</h4>
        <ul>
          <li>Login to your YouTube account.</li>
          <li>Upload a video or a short, or select an existing one.</li>
          <li>Right-click the video or short and select "Copy Link."</li>
          <li>Paste the link in the field below and click "Add."</li>
        </ul>
      </div>
      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}

      <div className={styles.form}>
        <input
          type="text"
          name="url"
          placeholder="Enter video URL"
          value={videoData.url}
          onChange={handleInputChange}
          className={styles.input}
        />
        <button
          onClick={addVideo}
          className={styles.button}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : editIndex !== null ? (
            "Update"
          ) : (
            "Add"
          )}
        </button>
      </div>

      <div className={styles.videosGrid}>
        {videos.map((video, index) => (
          <div key={index} className={styles.videoBox}>
            <iframe
              className={styles.video}
              src={getEmbedUrl(video.url)}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <div className={styles.videoInfo}>
              <p>
                <a href={video.url} target="_blank" rel="noopener noreferrer">
                  {video.url}
                </a>
              </p>
              <div className={styles.actions}>
                {/* <button
                  onClick={() => editVideo(index)}
                  className={styles.editBtn}
                >
                  Edit
                </button> */}
                <button
                  onClick={() => handleDeleteClick(video.id)}
                  className={styles.deleteBtn}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal for delete confirmation */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this YouTube short/video?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="secondary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default YoutubeVideoManager;
